import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Timestamp } from 'firebase/firestore';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

interface ChatMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  createdAt: Timestamp;
}

export interface ChatThread {
  id: string;
  title?: string;
  createdAt: any;
  lastMessageAt: any;
}

interface ChatContextType {
  threads: ChatThread[];
  currentThread: ChatThread | null;
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  sendMessage: (message: string) => Promise<void>;
  createNewThread: (title?: string) => Promise<ChatThread>;
  selectThread: (thread: ChatThread) => Promise<void>;
  refreshThreads: () => Promise<void>;
  updateThreadTitle: (threadId: string, newTitle: string) => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const functions = getFunctions();
  
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [currentThread, setCurrentThread] = useState<ChatThread | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createThread = httpsCallable(functions, 'createThread');
  const sendMessageFn = httpsCallable(functions, 'sendMessage');
  const getThreadsFn = httpsCallable(functions, 'getThreads');
  const getMessagesFn = httpsCallable(functions, 'getMessages');

  // Load threads when user logs in
  useEffect(() => {
    if (currentUser) {
      refreshThreads();
    } else {
      setThreads([]);
      setCurrentThread(null);
      setMessages([]);
    }
  }, [currentUser]);

  const refreshThreads = async () => {
    try {
      setLoading(true);
      const result = await getThreadsFn();
      const threadsData = result.data as { threads: ChatThread[] };
      setThreads(threadsData.threads);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createNewThread = async (title?: string) => {
    try {
      if (!currentUser) throw new Error('No user logged in');
      
      const threadsRef = collection(db, 'users', currentUser.uid, 'threads');
      const newThread = {
        title: title || `Chat ${new Date().toLocaleDateString()}`,
        createdAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
      };
      
      const threadDoc = await addDoc(threadsRef, newThread);
      const thread = { id: threadDoc.id, ...newThread };
      setThreads(prev => [thread, ...prev]);
      setCurrentThread(thread);
      setMessages([]);
      return thread;
    } catch (error) {
      console.error('Error creating thread:', error);
      setError('Failed to create new chat thread');
      throw error;
    }
  };

  const updateThreadTitle = async (threadId: string, newTitle: string) => {
    try {
      if (!currentUser) throw new Error('No user logged in');
      if (!newTitle.trim()) return;

      const threadRef = doc(db, 'users', currentUser.uid, 'threads', threadId);
      await updateDoc(threadRef, { title: newTitle.trim() });
      
      setThreads(prev => prev.map(thread => 
        thread.id === threadId ? { ...thread, title: newTitle.trim() } : thread
      ));
      
      if (currentThread?.id === threadId) {
        setCurrentThread(prev => prev ? { ...prev, title: newTitle.trim() } : null);
      }
    } catch (error) {
      console.error('Error updating thread title:', error);
      setError('Failed to update chat title');
      throw error;
    }
  };

  const selectThread = async (thread: ChatThread) => {
    try {
      setLoading(true);
      setCurrentThread(thread);
      const result = await getMessagesFn({ threadId: thread.id });
      const messagesData = result.data as { messages: ChatMessage[] };
      setMessages(messagesData.messages);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (message: string) => {
    if (!currentThread) {
      throw new Error('No active thread');
    }

    try {
      setLoading(true);
      const result = await sendMessageFn({
        threadId: currentThread.id,
        message,
      });
      const { response } = result.data as { response: string; threadId: string };
      
      // Refresh messages after sending
      const messagesResult = await getMessagesFn({ threadId: currentThread.id });
      const messagesData = messagesResult.data as { messages: ChatMessage[] };
      setMessages(messagesData.messages);
      
      // Refresh threads to update lastMessageAt
      await refreshThreads();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    threads,
    currentThread,
    messages,
    loading,
    error,
    sendMessage,
    createNewThread,
    selectThread,
    refreshThreads,
    updateThreadTitle,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}; 